import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Bell, CheckCircle2, AlertTriangle, Info, XCircle } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LEVEL = {
  success: { Icon: CheckCircle2, cls: "text-emerald-400" },
  warning: { Icon: AlertTriangle, cls: "text-violet-300" },
  error: { Icon: XCircle, cls: "text-rose-400" },
  info: { Icon: Info, cls: "text-sky-400" },
};

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = items.filter((n) => !n.read).length;

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const onOpenChange = (o) => {
    setOpen(o);
    if (o && unread > 0) {
      api.post("/notifications/read").catch(() => {});
      setTimeout(() => setItems((prev) => prev.map((n) => ({ ...n, read: true }))), 800);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" aria-label="Notifications"
          className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-unread-count"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[420px] overflow-y-auto p-0">
        <DropdownMenuLabel className="px-4 py-3 border-b border-border text-sm">Notifications</DropdownMenuLabel>
        {items.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="notification-empty">
            You're all caught up.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {items.map((n) => {
              const L = LEVEL[n.level] || LEVEL.info;
              return (
                <div key={n.id} data-testid={`notification-item-${n.id}`}
                  className={`flex items-start gap-3 px-4 py-3 ${n.read ? "opacity-60" : "bg-secondary/30"}`}>
                  <L.Icon className={`h-4 w-4 mt-0.5 shrink-0 ${L.cls}`} />
                  <div className="min-w-0">
                    <p className="text-sm text-foreground/90 leading-snug">{n.message}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {n.created_at ? new Date(n.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
